import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import format from 'date-fns/format';

import {
  answerBlock
} from 'actions';

import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import QuestionBlock from 'components/Questionnaires/QuestionBlock';
import Question from 'components/Questionnaires/Question';
import { QuestionAddressBlock, QuestionBlockRepeater, QuestionTaxResidenceBlock } from 'components/Questionnaires/blocks';
import { QuestionTextField, QuestionDateDropdownField, QuestionDropDownField, QuestionJurisdictionDropDownField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionControllingPersonBlock = ({ block, onAnswer, preQualAnswers }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const [firstName, setFirstName] = useState({
    id: 'firstName',
    label: 'questionnaire.crs.name.firstName.label',
    component: QuestionTextField,
    info: {
      instructions: 'questionnaire.crs.name.firstName.info'
    },
    className: 'col-12 col-lg-6',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.crs.name.firstName.errorMessage' }
      ]
    }
  });

  const [middleName, setMiddleName] = useState({
    id: 'middleName',
    label: 'questionnaire.crs.name.middleName.label',
    component: QuestionTextField,
    className: 'col-12 col-lg-6',
    visible: true,
  });

  const [lastName, setLastName] = useState({
    id: 'lastName',
    label: 'questionnaire.crs.name.lastName.label',
    component: QuestionTextField,
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.crs.name.lastName.errorMessage' }
      ]
    }
  });

  const [birthDate, setBirthDate] = useState({
    id: 'birthDate',
    label: 'questionnaire.birth.date.label',
    component: QuestionDateDropdownField,
    componentProps: {
      minDate: format(moment().subtract(125, 'years').toDate(), 'dd-MM-yyyy'),
      maxDate: format(moment().subtract(1, 'days').toDate(), 'dd-MM-yyyy'),
      dateFormat: 'dd-MM-yyyy',
      showToolTip: true
    },
    info: {
      instructions: 'questionnaire.birth.date.info'
    },
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.birth.date.errorMessage' }
      ]
    }
  });

  const [birthCountry, setBirthCountry] = useState({
    id: 'birthCountry',
    label: 'questionnaire.birth.country.label',
    component: QuestionJurisdictionDropDownField,
    className: 'col-12 col-lg-6',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.birth.country.errorMessage' }
      ]
    }
  });

  const [birthTown, setBirthTown] = useState({
    id: 'birthTown',
    label: 'questionnaire.birth.town.label',
    component: QuestionTextField,
    className: 'col-12 col-lg-6',
    visible: true,
  });

  const [type, setType] = useState({
    id: 'type',
    label: 'questionnaire.fatcacrs.e.cp.type.label',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.fatcacrs.e.cp.type.items'
    },
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.fatcacrs.e.cp.type.errorMessage' }
      ]
    }
  });

  const [address, setAddress] = useState({
    id: 'address',
    title: 'questionnaire.address',
    component: QuestionAddressBlock,
    info: {
      instructions: 'questionnaire.address.1.info'
    },
    className: 'p-0 no-border nested',
    linkBlock: true,
    visible: true,
  });

  const [mailingAddress, setMailingAddress] = useState({
    id: 'mailingAddress',
    title: 'questionnaire.mailingAddress',
    component: QuestionAddressBlock,
    componentProps: {
      line1Label: 'questionnaire.mailingAddress.1.label',
      optional: true
    },
    info: {
      instructions: 'questionnaire.mailingAddress.1.info'
    },
    className: 'p-0 no-border nested',
    linkBlock: true,
    visible: true
  });

  const [taxResidences, setTaxResidences] = useState({
    id: 'taxResidences',
    component: QuestionBlockRepeater,
    componentProps: {
      collapsible: true
    },
    className: 'no-border p-0',
    expandCollapse: false,
    repeatBlock: {
      id: 'taxResidence',
      title: 'questionnaire.taxResidence',
      component: QuestionTaxResidenceBlock,
      componentProps: {
        optional: true
      }
    },
    minSize: 0,
    maxSize: 3,
    addText: 'questionnaire.taxResidences.addLabel',
    visible: true,
  });

  const isEmptyObject = (obj) => {
    if (!obj) {
      return true;
    }
    let isEmpty = true;
    Object.keys(obj).forEach((key) => {
      if (obj[key]) {
        isEmpty = false;
      }
    });
    return isEmpty;
  };

  const isValid = () => {
    if (firstName.answer === undefined
      || lastName.answer === undefined
      || birthDate.answer === undefined
      || birthCountry.answer === undefined
      || type.answer === undefined
      || address.answer === undefined
      || taxResidences.answer === undefined) {
      return undefined;
    }
    return firstName.isValid
      && lastName.isValid
      && birthDate.isValid
      && birthCountry.isValid
      && type.isValid
      && (!isEmptyObject(address.answer) && address.isValid)
      && (!mailingAddress.answer || mailingAddress.isValid)
      && (!taxResidences.answer || taxResidences.answer.length > 0);
  };

  const onBlockAnswer = (b, a, v) => {
    switch (b.id) {
      case 'address': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setAddress(newBlock);
        setAnswer({ ...answer, address: a });
        break;
      }
      case 'mailingAddress': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setMailingAddress(newBlock);
        setAnswer({ ...answer, mailingAddress: a });
        break;
      }
      case 'taxResidences': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setTaxResidences(newBlock);
        setAnswer({ ...answer, taxResidences: a });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid()));
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'firstName': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setFirstName(newQuestion);
        setAnswer({ ...answer, firstName: value });
        break;
      }
      case 'middleName': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setMiddleName(newQuestion);
        setAnswer({ ...answer, middleName: value });
        break;
      }
      case 'lastName': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setLastName(newQuestion);
        setAnswer({ ...answer, lastName: value });
        break;
      }
      case 'birthDate': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setBirthDate(newQuestion);
        setAnswer({ ...answer, birthDate: value });
        break;
      }
      case 'birthCountry': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setBirthCountry(newQuestion);
        setAnswer({ ...answer, birthCountry: value });
        break;
      }
      case 'birthTown': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setBirthTown(newQuestion);
        setAnswer({ ...answer, birthTown: value });
        break;
      }
      case 'type': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setType(newQuestion);
        setAnswer({ ...answer, type: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.firstName) {
        onQuestionChange({ ...firstName, isForceEdited: true }, '');
      }
      if (!answer?.lastName) {
        onQuestionChange({ ...lastName, isForceEdited: true }, '');
      }
      if (!answer?.birthDate) {
        onQuestionChange({ ...birthDate, isForceEdited: true }, '');
      }
      if (!answer?.birthCountry) {
        onQuestionChange({ ...birthCountry, isForceEdited: true }, '');
      }
      if (!answer?.type) {
        onQuestionChange({ ...type, isForceEdited: true }, '');
      }
      if (!answer.address || isEmptyObject(answer.address)) {
        const newBlock = { ...address, ...{ isForceEdited: true } };
        setAddress(newBlock);
      }
      if (answer.mailingAddress) {
        const newBlock = { ...mailingAddress, ...{ isForceEdited: true } };
        setMailingAddress(newBlock);
      }
      if (!answer.taxResidences || answer.taxResidences.length > 0) {
        const newBlock = { ...taxResidences, ...{ isForceEdited: true } };
        setTaxResidences(newBlock);
      }
    }
  }, [block.isForceEdited]);

  return (<div className="questions row cp-section">
    <Question key={ firstName.id } blockId={ block.id } question={ firstName } { ...firstName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ middleName.id } blockId={ block.id } question={ middleName } { ...middleName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ lastName.id } blockId={ block.id } question={ lastName } { ...lastName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ birthDate.id } blockId={ block.id } question={ birthDate } { ...birthDate.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ birthCountry.id } blockId={ block.id } question={ birthCountry } { ...birthCountry.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ birthTown.id } blockId={ block.id } question={ birthTown } { ...birthTown.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ type.id } blockId={ block.id } question={ type } { ...type.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <div className="col-12">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ address.id }` } block={ address } onAnswer={ onBlockAnswer } />
    </div>
    <div className="col-12">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ mailingAddress.id }` } block={ mailingAddress } onAnswer={ onBlockAnswer } />
    </div>
    <div className="col-12 pb-4">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ taxResidences.id }` } block={ taxResidences } onAnswer={ onBlockAnswer } />
    </div>
  </div>);
};

QuestionControllingPersonBlock.displayName = 'QuestionControllingPersonBlock';

QuestionControllingPersonBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape()
};

QuestionControllingPersonBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionControllingPersonBlock;
